import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Provider } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { api_path } from '../../global';

const PUBLIC_REGISTRY_PATH = '/organization/public_registry/';
export const REGISTRY_SESSION_URL = `${api_path}/organization/public_registry/session`;
export const REGISTRY_SESSION_REQUIRED_STATUS = 428;
export const REGISTRY_SESSION_REQUIRED_CODE_NAME = 'REGISTRY_SESSION_REQUIRED';

/**
 * Makes sure the API has issued the signed HttpOnly registry cookie before calling the
 * public registry endpoints, and transparently renews it once when the API answers 428.
 */
@Injectable()
export class RegistryAccessInterceptor implements HttpInterceptor {
  private session$: Observable<boolean> | null = null;
  // Bypasses the interceptor chain, so the session call is not intercepted again
  private readonly httpClient: HttpClient;

  constructor(httpBackend: HttpBackend) {
    this.httpClient = new HttpClient(httpBackend);
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isPublicRegistryRequest(request)) {
      return next.handle(request);
    }

    return this.ensureSession().pipe(
      switchMap(() => next.handle(request)),
      catchError((error) =>
        this.isSessionRequiredError(error)
          ? this.refreshSession().pipe(switchMap(() => next.handle(request)))
          : throwError(() => error),
      ),
    );
  }

  private isPublicRegistryRequest(request: HttpRequest<any>): boolean {
    return request.url.includes(PUBLIC_REGISTRY_PATH) && request.url !== REGISTRY_SESSION_URL;
  }

  private isSessionRequiredError(error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      error.status === REGISTRY_SESSION_REQUIRED_STATUS &&
      error.error?.code_name === REGISTRY_SESSION_REQUIRED_CODE_NAME
    );
  }

  private ensureSession(): Observable<boolean> {
    if (!this.session$) {
      this.session$ = this.requestSession().pipe(shareReplay(1));
    }
    return this.session$;
  }

  private refreshSession(): Observable<boolean> {
    this.session$ = null;
    return this.ensureSession();
  }

  private requestSession(): Observable<boolean> {
    return this.httpClient.post(REGISTRY_SESSION_URL, null, { withCredentials: true }).pipe(
      map(() => true),
      // Never break the registry because of the session call; let the next request try again
      catchError(() => {
        this.session$ = null;
        return of(false);
      }),
    );
  }
}

export const RegistryAccessInterceptorService: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: RegistryAccessInterceptor,
  multi: true,
};
